import { Stack, styled, Input } from "prism-react";
import type { FC } from "react";
import { useState, useLayoutEffect, useEffect, useRef } from "react";
import tinykeys from "tinykeys";

import { useValidateProjectName } from "./api";
import type { UseValidateProjectNameReturnType } from "./types";

const InputContainer = styled("div", {
  position: "relative",
});

const InputError = styled("div", {
  color: "$informative-error-base",
  minHeight: "$4",
});

interface RepositoryNameInputProps {
  onChange?: (name: string) => void;
  onValidation?: (validation: UseValidateProjectNameReturnType) => void;
  defaultValue: string;
  disabled?: boolean;
  organizationName: string;
}

export const RepositoryNameInput: FC<RepositoryNameInputProps> = ({
  onChange,
  defaultValue,
  disabled,
  organizationName,
  onValidation,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const validation = useValidateProjectName(organizationName, inputValue);

  useEffect(() => {
    if (!inputRef.current) return;
    return tinykeys(inputRef.current, {
      Escape: (e) => {
        if (onChange) {
          onChange("");
        }
        setInputValue("");

        e.stopPropagation();
      },
    });
  }, [onChange]);

  useEffect(() => {
    if (onValidation) {
      onValidation(validation);
    }
  }, [validation.status]);

  useLayoutEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Stack>
      <InputContainer>
        <Input
          ref={inputRef}
          css={{ width: "100%" }}
          disabled={disabled}
          icon={
            validation.status === "error"
              ? "circleBang"
              : validation.status === "valid"
                ? "check"
                : "progress"
          }
          name="repositoryName"
          onChange={(e) => {
            setInputValue(e.target.value);
            if (onChange) {
              onChange(e.target.value);
            }
          }}
          placeholder={"Enter project name"}
          value={inputValue}
        />
      </InputContainer>
      {!disabled && validation.status === "error" ? (
        <InputError>{validation.error}</InputError>
      ) : null}
    </Stack>
  );
};
